import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Grid } from '@mui/material';

import useDebouncedForm from 'common/hooks/useDebouncedForm';
import EmptyState from 'common/components/EmptyState';

import ProductSearch from 'views/Product/__components/ProductSearch';
import ProductList from 'views/Product/__components/ProductList';

import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { ProductApi } from 'common/types';

const SetProducts = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const productsById = useSelector(StockSelectors.stockProductsById);
	const loading = useSelector(StockSelectors.stockProductsLoading);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				setProductIds: product?.setProductIds ?? [],
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const handleAdd = useCallback(
		(product) => {
			editState({
				setProductIds: state.setProductIds.concat(product.id),
			});
		},
		[editState, state.setProductIds],
	);

	const handleChange = useCallback(
		(products: ProductApi[]) => {
			editState({
				setProductIds: products.map((p) => p.id),
			});
		},
		[editState],
	);

	const setProducts = useMemo(() => {
		return state.setProductIds.map((id) => productsById[id]).filter((product) => !!product);
	}, [state.setProductIds, productsById]);

	const isEmpty = setProducts.length === 0;

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="h6">
					{t('product.productPackageItemsTitle', 'Products in package')}
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<ProductSearch
					onSelect={handleAdd}
					hiddenIds={state.setProductIds.concat(product?.id ?? [])}
					hideSetProducts
					placeholder={t('productSettings.searchProducts', 'Search products')}
					disabled={isEditingDisabled}
				/>
				<EmptyState
					isEmpty={isEmpty}
					isLoading={loading}
					render={() => (
						<ProductList
							products={setProducts}
							disabled={isEditingDisabled}
							onChange={handleChange}
						/>
					)}
				/>
			</Grid>
		</Grid>
	);
};

export default SetProducts;
